/**
 * Utilities for working with check-in data
 */

/**
 * Get the date from a check-in, handling both 'date' and 'timestamp' fields
 * for backwards compatibility
 */
export function getCheckinDate(checkin: any): string {
  return checkin.date || checkin.timestamp || new Date().toISOString();
}

/**
 * Get the RTI score from a check-in, with fallback
 */
export function getCheckinRTI(checkin: any): number {
  return typeof checkin.rtiScore === 'number' ? checkin.rtiScore : 0;
}

/**
 * Ensure check-in has required fields
 */
export function normalizeCheckin(checkin: any): any {
  const normalized = { ...checkin };
  
  // Ensure date field exists
  if (!normalized.date && normalized.timestamp) {
    normalized.date = normalized.timestamp;
  }
  
  // Ensure timestamp field exists (backwards compatibility)
  if (!normalized.timestamp && normalized.date) {
    normalized.timestamp = normalized.date;
  }
  
  // Ensure rtiScore exists
  if (typeof normalized.rtiScore !== 'number') {
    normalized.rtiScore = 0;
  }
  
  return normalized;
}

/**
 * Sort check-ins by date (newest first)
 */
export function sortCheckinsByDate(checkins: any[], descending = true): any[] {
  return [...checkins].sort((a, b) => {
    const dateA = new Date(getCheckinDate(a)).getTime();
    const dateB = new Date(getCheckinDate(b)).getTime();
    return descending ? dateB - dateA : dateA - dateB;
  });
}

/**
 * Filter check-ins by date range
 */
export function filterCheckinsByDateRange(
  checkins: any[],
  startDate: Date,
  endDate: Date
): any[] {
  return checkins.filter(checkin => {
    const date = new Date(getCheckinDate(checkin));
    return date >= startDate && date <= endDate;
  });
}

/**
 * Get check-ins for a specific relationship
 */
export function getCheckinsForRelationship(
  checkins: any[],
  relationshipId: string
): any[] {
  return checkins.filter(checkin => checkin.relationshipId === relationshipId);
}

/**
 * Calculate average RTI for a set of check-ins
 */
export function calculateAverageRTI(checkins: any[]): number {
  if (checkins.length === 0) return 0;
  const sum = checkins.reduce((acc, checkin) => acc + getCheckinRTI(checkin), 0);
  return Math.round(sum / checkins.length);
}

/**
 * Get RTI state from score
 */
export function getRTIState(score: number): 'Stable' | 'Elastic' | 'High Tension' {
  if (score <= 35) return 'Stable';
  if (score <= 60) return 'Elastic';
  return 'High Tension';
}

/**
 * Get color class for RTI score
 */
export function getRTIColor(score: number): string {
  if (score <= 35) return 'text-green-600 dark:text-green-400';
  if (score <= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

/**
 * Get background color class for RTI score
 */
export function getRTIBackgroundColor(score: number): string {
  if (score <= 35) return 'bg-green-100 dark:bg-green-900';
  if (score <= 60) return 'bg-amber-100 dark:bg-amber-900';
  return 'bg-red-100 dark:bg-red-900';
}
